import { usePageTitle } from "@/hooks/use-page-title";
import { useTranslation } from "react-i18next";
import { useFinancialTransparencyStats, usePlatformFeeRates, useIncentiveLedger } from "@/hooks/use-queries";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  DollarSign, 
  TrendingUp, 
  PieChart, 
  FileText, 
  Download,
  Heart,
  Users,
  Shield,
  BarChart3,
  CircleDollarSign,
  Wallet as WalletIcon,
  ArrowUpRight,
  ArrowDownRight,
  HelpCircle,
  ExternalLink,
  Percent
} from "lucide-react";
import { format } from "date-fns";
import { PageAbout } from "@/components/page-about";

const FLOW_TYPE_KEYS: Record<string, string> = {
  wallet_contribution: "financial-transparency.flow-type.wallet-contribution",
  wallet_subscription: "financial-transparency.flow-type.wallet-subscription",
  community_fund_contribution: "financial-transparency.flow-type.community-fund-contribution",
  condition_wallet_contribution: "financial-transparency.flow-type.condition-wallet-contribution",
  emergency_care_contribution: "financial-transparency.flow-type.emergency-care-contribution",
  precare_subscription: "financial-transparency.flow-type.precare-subscription",
  payment_schedule: "financial-transparency.flow-type.payment-schedule",
};

export default function FinancialTransparencyPage() {
  usePageTitle("Financial Transparency", "Real-time financial reporting and transparency metrics for HealthWallet community funds.");
  const { t } = useTranslation();

  const { data: stats, isLoading: statsLoading } = useFinancialTransparencyStats();
  const { data: feeRates, isLoading: ratesLoading } = usePlatformFeeRates();
  const { data: incentiveLedger = [] } = useIncentiveLedger();
  const transactions = stats?.recentTransactions || [];

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const handleDownloadCsv = () => {
    const url = "/api/export/transactions?format=csv";
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const getCategoryIcon = (category: string) => {
    const lower = category.toLowerCase();
    if (lower.includes("preventive") || lower.includes("wellness")) return <Shield className="h-4 w-4" />;
    if (lower.includes("specialty") || lower.includes("treatment")) return <BarChart3 className="h-4 w-4" />;
    if (lower.includes("community")) return <Heart className="h-4 w-4" />;
    return <DollarSign className="h-4 w-4" />;
  };

  const getCategoryColor = (category: string) => {
    const lower = category.toLowerCase();
    if (lower.includes("healthcare") || lower.includes("payment")) return "bg-blue-500";
    if (lower.includes("specialty")) return "bg-purple-500";
    if (lower.includes("preventive")) return "bg-orange-500";
    if (lower.includes("community")) return "bg-pink-500";
    return "bg-slate-500";
  };

  const platformBreakdown = buildPlatformBreakdown(feeRates);

  const financialResources = [
    {
      title: t('financial-transparency.resources.understanding-wallet'),
      description: t('financial-transparency.resources.understanding-wallet-desc'),
      type: "guide"
    },
    {
      title: t('financial-transparency.resources.cost-assistance'),
      description: t('financial-transparency.resources.cost-assistance-desc'),
      type: "resource"
    },
    {
      title: t('financial-transparency.resources.tax-deduction'),
      description: t('financial-transparency.resources.tax-deduction-desc'),
      type: "tax"
    },
    {
      title: t('financial-transparency.resources.insurance-info'),
      description: t('financial-transparency.resources.insurance-info-desc'),
      type: "insurance"
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="page-financial-transparency">
      <PageAbout
        title={t('financial-transparency.heading')}
        description={t('financial-transparency.about-desc')}
        features={[
          t('financial-transparency.about.feature-1'),
          t('financial-transparency.about.feature-2'),
          t('financial-transparency.about.feature-3'),
          t('financial-transparency.about.feature-4'),
          t('financial-transparency.about.feature-5'),
        ]}
        tips={[
          t('financial-transparency.about.tip-1'),
          t('financial-transparency.about.tip-2'),
          t('financial-transparency.about.tip-3'),
        ]}
      />
      
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="text-page-title">
            {t('financial-transparency.heading')}
          </h1>
          <p className="text-muted-foreground">
            {t('financial-transparency.subheading')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadCsv} data-testid="button-download-statement">
            <Download className="h-4 w-4 mr-2" />
            {t('common.cta.download-statement')}
          </Button>
          <Button variant="outline" size="sm" data-testid="button-view-help">
            <HelpCircle className="h-4 w-4 mr-2" />
            {t('common.help')}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        {statsLoading ? (
          <>
            {[1, 2, 3, 4].map(i => (
              <Card key={i}><CardContent className="pt-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
            ))}
          </>
        ) : (
          <>
            <Card data-testid="card-total-contributed">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">{t('financial-transparency.stat-total-contributed')}</CardTitle>
                <CircleDollarSign className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold" data-testid="text-total-contributed">
                  {formatCurrency(stats?.totalContributed || 0)}
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {t('financial-transparency.stat-since', { date: stats?.memberSince ? format(new Date(stats.memberSince), "MMM yyyy") : "N/A" })}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-total-spent">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">{t('financial-transparency.stat-total-spent')}</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold" data-testid="text-total-spent">
                  {formatCurrency(stats?.totalSpent || 0)}
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {t('financial-transparency.stat-payments-count', { count: stats?.totalPayments || 0 })}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-current-balance">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">{t('financial-transparency.stat-current-balance')}</CardTitle>
                <WalletIcon className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold text-primary" data-testid="text-current-balance">
                  {formatCurrency(stats?.currentBalance || 0)}
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {t('financial-transparency.stat-available-healthcare')}
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-community-impact">
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">{t('financial-transparency.stat-community-impact')}</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground hidden sm:block" />
              </CardHeader>
              <CardContent>
                <div className="text-xl sm:text-2xl font-bold text-green-600" data-testid="text-community-impact">
                  {formatCurrency(stats?.communityImpact || 0)}
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground">
                  {t('financial-transparency.stat-helped-others')}
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Tabs defaultValue="breakdown" className="space-y-4">
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <TabsList className="w-full sm:w-auto inline-flex">
            <TabsTrigger value="breakdown" className="text-xs sm:text-sm" data-testid="tab-breakdown">{t('financial-transparency.tab.spending')}</TabsTrigger>
            <TabsTrigger value="platform" className="text-xs sm:text-sm" data-testid="tab-platform">{t('financial-transparency.tab.platform')}</TabsTrigger>
            <TabsTrigger value="history" className="text-xs sm:text-sm" data-testid="tab-history">{t('financial-transparency.tab.history')}</TabsTrigger>
            <TabsTrigger value="resources" className="text-xs sm:text-sm" data-testid="tab-resources">{t('financial-transparency.tab.resources')}</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="breakdown" className="space-y-4">
          <h2 className="text-lg font-semibold">{t('financial-transparency.spending.section-heading')}</h2>

          <div className="grid gap-4 md:grid-cols-2">
            <Card data-testid="card-spending-categories">
              <CardHeader>
                <CardTitle className="text-base">{t('financial-transparency.spending.categories-heading')}</CardTitle>
                <CardDescription>{t('financial-transparency.spending.categories-desc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {statsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                  </div>
                ) : stats?.spendingCategories && stats.spendingCategories.length > 0 ? (
                  stats.spendingCategories.map((category, index) => (
                    <div key={index} className="space-y-2" data-testid={`category-${index}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${getCategoryColor(category.category)} text-white`}>
                            {getCategoryIcon(category.category)}
                          </div>
                          <span className="text-sm font-medium">{category.category}</span>
                        </div>
                        <span className="text-sm font-semibold">{formatCurrency(category.amount)}</span>
                      </div>
                      <Progress value={category.percentage} className="h-2" />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <PieChart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('financial-transparency.spending.empty-state')}</p>
                    <p className="text-xs">{t('financial-transparency.spending.empty-state-hint')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="card-spending-summary">
              <CardHeader>
                <CardTitle className="text-base">{t('financial-transparency.spending.summary-heading')}</CardTitle>
                <CardDescription>{t('financial-transparency.spending.summary-desc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className="h-40 w-full" />
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-sm">{t('financial-transparency.spending.active-subscriptions')}</span>
                      <span className="font-semibold">{stats?.activeSubscriptions || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-sm">{t('financial-transparency.spending.total-payments')}</span>
                      <span className="font-semibold">{stats?.totalPayments || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-sm">{t('financial-transparency.spending.member-since')}</span>
                      <span className="font-semibold">
                        {stats?.memberSince ? format(new Date(stats.memberSince), "MMM yyyy") : "N/A"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <span className="text-sm">{t('financial-transparency.spending.categories-label')}</span>
                      <span className="font-semibold">{stats?.spendingCategories?.length || 0}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="platform" className="space-y-4">
          <Card data-testid="card-platform-breakdown">
            <CardHeader>
              <CardTitle>{t('financial-transparency.platform.heading')}</CardTitle>
              <CardDescription>
                {t('financial-transparency.platform.desc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {ratesLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : (
                <>
                  {platformBreakdown.map((item, index) => (
                    <div key={index} className="space-y-2" data-testid={`platform-item-${index}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium">{t(item.labelKey)}</span>
                          <p className="text-xs text-muted-foreground">{t(item.descKey)}</p>
                        </div>
                        <span className="text-lg font-bold" data-testid={`platform-percentage-${index}`}>{item.percentage}%</span>
                      </div>
                      <Progress value={item.percentage} className="h-3" />
                    </div>
                  ))}

                  {feeRates && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          {t('financial-transparency.platform.fee-rates-heading')}
                        </h4>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {Object.entries(feeRates).map(([flowType, rate]) => (
                            <div
                              key={flowType}
                              className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                              data-testid={`fee-rate-display-${flowType}`}
                            >
                              <span className="text-sm">{FLOW_TYPE_KEYS[flowType] ? t(FLOW_TYPE_KEYS[flowType]) : flowType}</span>
                              <Badge variant="outline">{Number(rate)}%</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              
              <Separator />
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium">{t('financial-transparency.platform.security-heading')}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t('financial-transparency.platform.security-desc')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card data-testid="card-transaction-history">
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div>
                <CardTitle>{t('financial-transparency.history.heading')}</CardTitle>
                <CardDescription>{t('financial-transparency.history.desc')}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadCsv} data-testid="button-export-csv">
                <FileText className="h-4 w-4 mr-2" />
                {t('common.cta.export-csv')}
              </Button>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 && incentiveLedger.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{t('financial-transparency.history.empty-state')}</p>
                  <p className="text-sm">{t('financial-transparency.history.empty-state-hint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...transactions, ...incentiveLedger.map((entry: any) => ({
                    id: entry.id,
                    type: "incentive" as const,
                    amount: entry.amount,
                    description: entry.description,
                    createdAt: entry.createdAt
                  }))].slice(0, 10).map((item: any, index: number) => (
                    <div 
                      key={`${item.id}-${index}`} 
                      className="flex items-center justify-between gap-2 p-2 sm:p-3 rounded-lg border"
                      data-testid={`transaction-${index}`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div className={`p-1.5 sm:p-2 rounded-full shrink-0 ${
                          item.type === "contribution" || item.type === "subscription" 
                            ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" 
                            : item.type === "spend" || item.type === "order_payment"
                              ? "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                              : "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                        }`}>
                          {item.type === "contribution" || item.type === "subscription" ? (
                            <ArrowDownRight className="h-4 w-4" />
                          ) : item.type === "spend" || item.type === "order_payment" ? (
                            <ArrowUpRight className="h-4 w-4" />
                          ) : (
                            <TrendingUp className="h-4 w-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-xs sm:text-sm truncate">{item.description}</p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground">
                            {item.createdAt ? format(new Date(item.createdAt), "MMM d, yyyy") : "N/A"}
                          </p>
                        </div>
                      </div>
                      <span className={`font-semibold text-xs sm:text-sm shrink-0 ${
                        item.type === "contribution" || item.type === "subscription"
                          ? "text-green-600"
                          : item.type === "spend" || item.type === "order_payment"
                            ? "text-red-600"
                            : "text-blue-600"
                      }`}>
                        {item.type === "spend" || item.type === "order_payment" ? "-" : "+"}
                        {formatCurrency(Math.abs(item.amount))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {financialResources.map((resource, index) => (
              <Card key={index} className="hover-elevate cursor-pointer" data-testid={`resource-${index}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {resource.type === "guide" && <FileText className="h-5 w-5 text-blue-500" />}
                      {resource.type === "resource" && <Heart className="h-5 w-5 text-pink-500" />}
                      {resource.type === "tax" && <DollarSign className="h-5 w-5 text-green-500" />}
                      {resource.type === "insurance" && <Shield className="h-5 w-5 text-purple-500" />}
                      <CardTitle className="text-base">{resource.title}</CardTitle>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardDescription>{resource.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

          <Card data-testid="card-contact-support">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-primary/10">
                    <HelpCircle className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('financial-transparency.resources.help-heading')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('financial-transparency.resources.help-desc')}
                    </p>
                  </div>
                </div>
                <Button data-testid="button-contact-counselor">
                  {t('financial-transparency.resources.contact-counselor')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Card data-testid="card-incentive-summary">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t('financial-transparency.incentive.heading')}
          </CardTitle>
          <CardDescription>
            {t('financial-transparency.incentive.desc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:gap-4 grid-cols-3">
            <div className="text-center p-3 sm:p-4 bg-muted/50 rounded-lg">
              <p className="text-xl sm:text-3xl font-bold text-primary" data-testid="text-total-incentives">
                {formatCurrency(incentiveLedger.reduce((sum: number, e: any) => sum + e.amount, 0))}
              </p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">{t('financial-transparency.incentive.total-earned')}</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-muted/50 rounded-lg">
              <p className="text-xl sm:text-3xl font-bold text-green-600" data-testid="text-credits-applied">
                {incentiveLedger.filter((e: any) => e.status === "credited").length}
              </p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">{t('financial-transparency.incentive.credits-applied')}</p>
            </div>
            <div className="text-center p-3 sm:p-4 bg-muted/50 rounded-lg">
              <p className="text-xl sm:text-3xl font-bold text-orange-600" data-testid="text-pending-credits">
                {formatCurrency(incentiveLedger.filter((e: any) => e.status === "pending").reduce((sum: number, e: any) => sum + e.amount, 0))}
              </p>
              <p className="text-[10px] sm:text-sm text-muted-foreground">{t('financial-transparency.incentive.pending')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function buildPlatformBreakdown(feeRates: Record<string, number> | undefined) {
  if (!feeRates) {
    return [
      { labelKey: "financial-transparency.platform.direct-care-label", percentage: 97, descKey: "financial-transparency.platform.direct-care-desc" },
      { labelKey: "financial-transparency.platform.service-fee-label", percentage: 3, descKey: "financial-transparency.platform.service-fee-desc" },
    ];
  }

  const avgFeeRate = Object.values(feeRates).reduce((sum, r) => sum + r, 0) / Object.values(feeRates).length;
  const roundedFee = Math.round(avgFeeRate * 10) / 10;
  const directCare = Math.round((100 - roundedFee) * 10) / 10;

  return [
    { labelKey: "financial-transparency.platform.direct-care-label", percentage: directCare, descKey: "financial-transparency.platform.direct-care-desc" },
    { labelKey: "financial-transparency.platform.service-fee-label", percentage: roundedFee, descKey: "financial-transparency.platform.service-fee-desc" },
  ];
}
